import { PageOrderEnum } from "@/lib/enum/pageOrder.enum";
import customFieldService from "@/services/mrfiktiv/services/customFieldService";
import {
  MrfiktivCustomFieldConfigurationViewModelGen,
  MrfiktivCustomFieldControllerGetParamsGen,
  MrfiktivCustomFieldViewModelGen,
  MrfiktivPageViewModelGen,
  MrfiktivUpdateCustomFieldDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import store from "@/store/VuexPlugin";
import { Action, getModule, Module, Mutation } from "vuex-module-decorators";
import { MrfiktivCreateCustomFieldDtoGen } from "../../services/mrfiktiv/v1/data-contracts";
import { BasePagination, FilterTypes, IPaginationFilterListElement } from "./base-pagination.store";

/**
 * Possible custom fields
 */
export enum CustomFieldEnum {
  /**
   * A custom field that is a select option.
   * Only one value is displayed based on the configured values.
   */
  SINGLE_SELECT = "single_select",

  /**
   * A custom field storing a date.
   * Date values can be compared > < ==
   */
  DATE = "date",

  /**
   * A custom fields storing a number value.
   * Number values can be compared > < == etc.
   */
  NUMBER = "number",

  /**
   * A custom field storing a string value.
   */
  TEXT = "text"
}

export const customFieldIconTypeMap = new Map([
  [CustomFieldEnum.SINGLE_SELECT, "mdi-form-select"],
  [CustomFieldEnum.DATE, "mdi-calendar"],
  [CustomFieldEnum.NUMBER, "mdi-counter"],
  [CustomFieldEnum.TEXT, "mdi-form-textbox"]
]);

export class CustomFieldCreateDtoHelper implements MrfiktivCreateCustomFieldDtoGen {
  type: CustomFieldEnum;
  name: string;
  key: string;
  label: string;
  hint: string;
  placeholder: string;
  configuration?: MrfiktivCustomFieldConfigurationViewModelGen;

  constructor(customField?: Partial<MrfiktivCustomFieldViewModelGen>) {
    this.name = customField?.name ?? "";
    this.key = customField?.key ?? "";
    this.label = customField?.label ?? "";
    this.hint = customField?.hint ?? "";
    this.placeholder = customField?.placeholder ?? "";

    this.type = (customField?.type ?? CustomFieldEnum.TEXT) as CustomFieldEnum;
    if (customField?.type === CustomFieldEnum.SINGLE_SELECT) {
      this.configuration = customField?.configuration;
    }
  }
}
export class CustomFieldHelper extends CustomFieldCreateDtoHelper {
  id: string;
  partnerId: string;

  constructor(customField?: Partial<MrfiktivCustomFieldViewModelGen>) {
    super(customField);
    this.id = customField?.id ?? "";
    this.partnerId = customField?.partnerId ?? "";
  }
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "custom-field",
  store
})
export class CustomFieldStore extends BasePagination<
  MrfiktivCustomFieldViewModelGen,
  MrfiktivCustomFieldControllerGetParamsGen
> {
  protected _pageOrder: PageOrderEnum = PageOrderEnum.DESCENDING;
  protected _itemsPerPage = 100;
  protected _totalPages = 0;
  protected _paginationList: MrfiktivCustomFieldViewModelGen[] = [];
  protected _currentPage = 1;
  protected _totalItems = 0;
  protected _isLoadAll = true;
  filterOptions: IPaginationFilterListElement[] = [
    { key: "name", type: FilterTypes.STRING },
    { key: "type", type: FilterTypes.ENUM, config: { items: Object.values(CustomFieldEnum) } },
    { key: "label", type: FilterTypes.STRING },
    { key: "hint", type: FilterTypes.STRING },
    { key: "_id", type: FilterTypes.OBJECT_ID }
  ];

  private _customField: MrfiktivCustomFieldViewModelGen = new CustomFieldHelper();

  get customFields() {
    return this._paginationList;
  }

  @Mutation
  private _mutateCustomField(request: MrfiktivCustomFieldViewModelGen) {
    this._customField = request;
  }

  @Action
  protected async loadDocuments(
    query: MrfiktivCustomFieldControllerGetParamsGen
  ): Promise<
    MrfiktivPageViewModelGen & {
      data?: MrfiktivCustomFieldViewModelGen[] | undefined;
    }
  > {
    return customFieldService.get(query);
  }

  @Action
  async create(data: { partnerId: string; data: MrfiktivCreateCustomFieldDtoGen }) {
    const res = await customFieldService.create(data.partnerId, data.data);

    this.context.commit("_mutateCustomField", res);

    return res;
  }

  @Action
  async delete(data: { partnerId: string; customFieldId: string }) {
    const res = await customFieldService.delete(data.partnerId, data.customFieldId);
    this.context.commit("_mutateCustomField", res);

    this.removeInList(res);

    return res;
  }

  @Action
  async getOne(data: { partnerId: string; customFieldId: string }) {
    const res = await customFieldService.getOne(data.partnerId, data.customFieldId);

    this.context.commit("_mutateCustomField", res);

    return res;
  }

  @Action
  async update(data: { partnerId: string; customFieldId: string; data: MrfiktivUpdateCustomFieldDtoGen }) {
    const res = await customFieldService.update(data.partnerId, data.customFieldId, data.data);

    this.context.commit("_mutateCustomField", res);

    return res;
  }
}

export const CustomFieldModule = getModule(CustomFieldStore);
